// ═══════════════════════════════════════════════════
//  Shared currency conversion for hardware costs
//  Lifted verbatim from the private copy in arm-cost.ts so the two can never
//  drift. seat-cushion-cost.ts and back-cushion-cost.ts still carry their own
//  identical copies; folding those in is a separate cleanup.
// ═══════════════════════════════════════════════════

/** Currency context from project settings for MXN → USD conversion. */
export interface HardwareCurrencyContext {
  mxn_exchange_rate: number | null;
  mxn_bank_spread: number;
}

/** The subset of a Hardware Library record that costing needs. */
export interface HardwareCostEntry {
  cost_per_unit: number;
  name: string;
  unit?: string;
  currency?: string;
  iva_percent?: number;
  /** Minutes to install one unit. Null/absent means "not measured". */
  install_minutes?: number | null;
  /** Flat USD cost to ship one unit. Absent means 0 — not converted, not
   *  subject to iva_percent. */
  shipping_cost?: number;
}

/**
 * Every field of a Hardware Library record that costing reads, in one place.
 *
 * Both the Cria Library cost hook and the hardware template modal need this
 * map. They used to build it by hand, and a field added to one was missed by
 * the other — install_minutes silently defaulted to "not measured" on the
 * library page while the modal showed it correctly. Build it here or the same
 * bug comes back.
 */
export function buildHardwareCostMap(hardwareList: any[]): Map<string, HardwareCostEntry> {
  const map = new Map<string, HardwareCostEntry>();
  for (const h of hardwareList ?? []) {
    map.set(h.id, {
      cost_per_unit: h.cost_per_unit ?? 0,
      name: h.name,
      unit: h.unit,
      currency: h.currency,
      iva_percent: h.iva_percent,
      install_minutes: h.install_minutes ?? null,
      shipping_cost: h.shipping_cost ?? 0,
    });
  }
  return map;
}

/**
 * IVA first, then MXN → USD at (rate − spread).
 *
 * When no currency context is supplied — as in the Cria Library, which has no
 * project and therefore no exchange rate — an MXN cost is returned at face
 * value. Callers that care must surface that to the user.
 */
export function convertHardwareCost(
  hw: HardwareCostEntry,
  currencyCtx?: HardwareCurrencyContext,
): { cost_per_unit: number; currency_note?: string } {
  let costPerUnit = hw.cost_per_unit ?? 0;
  const currency = hw.currency || 'USD';
  const ivaPercent = hw.iva_percent || 0;

  if (ivaPercent > 0) {
    costPerUnit = costPerUnit * (1 + ivaPercent / 100);
  }

  if (currency === 'MXN' && currencyCtx?.mxn_exchange_rate) {
    const effectiveRate = currencyCtx.mxn_exchange_rate - (currencyCtx.mxn_bank_spread || 0);
    if (effectiveRate > 0) {
      const originalMxn = costPerUnit;
      costPerUnit = costPerUnit / effectiveRate;
      const note = `MXN $${originalMxn.toFixed(2)} ÷ ${effectiveRate.toFixed(4)} = $${costPerUnit.toFixed(4)} USD`;
      return { cost_per_unit: costPerUnit, currency_note: note };
    }
  }

  return { cost_per_unit: costPerUnit };
}
